// Suppression différée avec annulation. Le DELETE ne part qu'à l'expiration
// du toast (5 s) : les FK des commentaires/PJ/tags sont en ON DELETE CASCADE,
// une recréation côté client perdrait ces données. Timers en portée module :
// ils survivent aux démontages de composants (fermer la modale n'annule pas
// la suppression en cours).
import { toast } from 'sonner';
import { useDeleteTask } from './queries/useTasks';
import { useTasksStore } from '../store/tasksStore';

const UNDO_MS = 5000;
const timers = new Map<string, ReturnType<typeof setTimeout>>();

interface UndoableDeleteDeps {
  deleteTask: ReturnType<typeof useDeleteTask>;
  markPendingDelete: (id: string) => void;
  unmarkPendingDelete: (id: string) => void;
}

// Logique unitaire partagée : appelée directement par requestDelete, et par
// useUndoableBulkDelete quand une seule tâche est concernée (libellé avec le
// titre plutôt que le compteur générique).
function requestDeleteOne(deps: UndoableDeleteDeps, task: { id: string; content: string }) {
  const { deleteTask, markPendingDelete, unmarkPendingDelete } = deps;
  if (timers.has(task.id)) return; // déjà en attente
  markPendingDelete(task.id);

  const toastId = toast(`Tâche supprimée — « ${task.content} »`, {
    duration: UNDO_MS,
    action: {
      label: 'Annuler',
      onClick: () => {
        const timer = timers.get(task.id);
        if (timer) clearTimeout(timer);
        timers.delete(task.id);
        unmarkPendingDelete(task.id);
      },
    },
  });

  timers.set(
    task.id,
    setTimeout(() => {
      timers.delete(task.id);
      toast.dismiss(toastId);
      deleteTask.mutate(task.id, {
        onSettled: () => unmarkPendingDelete(task.id),
        onError: () => toast.error('La suppression a échoué — tâche restaurée.'),
      });
    }, UNDO_MS),
  );
}

export function useUndoableTaskDelete() {
  const deleteTask = useDeleteTask();
  const markPendingDelete = useTasksStore((s) => s.markPendingDelete);
  const unmarkPendingDelete = useTasksStore((s) => s.unmarkPendingDelete);

  return function requestDelete(task: { id: string; content: string }) {
    requestDeleteOne({ deleteTask, markPendingDelete, unmarkPendingDelete }, task);
  };
}

export function useUndoableBulkDelete() {
  const deleteTask = useDeleteTask();
  const markPendingDelete = useTasksStore((s) => s.markPendingDelete);
  const unmarkPendingDelete = useTasksStore((s) => s.unmarkPendingDelete);

  return function requestDeleteMany(tasks: { id: string; content: string }[]) {
    const fresh = tasks.filter((t) => !timers.has(t.id));
    if (fresh.length === 0) return;
    const [only] = fresh;
    if (fresh.length === 1 && only) {
      // Retombe sur le toast unitaire existant (libellé avec le titre).
      requestDeleteOne({ deleteTask, markPendingDelete, unmarkPendingDelete }, only);
      return;
    }
    fresh.forEach((t) => markPendingDelete(t.id));

    const toastId = toast(
      `${fresh.length} tâche${fresh.length > 1 ? 's' : ''} supprimée${fresh.length > 1 ? 's' : ''}`,
      {
        duration: UNDO_MS,
        action: {
          label: 'Annuler',
          onClick: () => {
            fresh.forEach((t) => {
              const timer = timers.get(t.id);
              if (timer) clearTimeout(timer);
              timers.delete(t.id);
              unmarkPendingDelete(t.id);
            });
          },
        },
      },
    );

    fresh.forEach((t, i) => {
      timers.set(
        t.id,
        setTimeout(() => {
          timers.delete(t.id);
          if (i === 0) toast.dismiss(toastId);
          deleteTask.mutate(t.id, {
            onSettled: () => unmarkPendingDelete(t.id),
            onError: () => toast.error('La suppression a échoué — tâche restaurée.'),
          });
        }, UNDO_MS),
      );
    });
  };
}
